import { useState, useMemo } from 'react'
import PageHeader from '../components/PageHeader.jsx'
import { useAccounts } from '../features/customer-journey/useAccounts.js'
import JourneyFunnel from '../features/customer-journey/JourneyFunnel.jsx'
import AccountBoard from '../features/customer-journey/AccountBoard.jsx'
import AccountDetail from '../features/customer-journey/AccountDetail.jsx'
import RepBar from '../features/customer-journey/RepBar.jsx'
import LaneBoard from '../features/customer-journey/LaneBoard.jsx'
import Segmented from '../components/Segmented.jsx'
import ErrorBoundary from '../components/ErrorBoundary.jsx'
import PurposeStrip from '../components/PurposeStrip.jsx'
import { INTERNAL_LABEL } from '../lib/surfaces.js'
import { countByPhase, filterDeals, repRollup, PHASE_LABEL } from '../lib/journey.js'
import JourneyCoverageNotice from '../features/customer-journey/JourneyCoverageNotice.jsx'
import DateFilter from '../components/DateFilter.jsx'
import { useDateFilter } from '../lib/useDateFilter.js'
import styles from './CustomerJourney.module.css'

/**
 * Customer Journey — the live order pipeline, read-only.
 *
 * The page ALWAYS renders its real chrome (funnel, board, detail). A feed that
 * isn't reachable degrades into an honest notice inside the page; it never
 * short-circuits the route into a blank screen, and it never substitutes
 * invented rows for missing data.
 */
export default function CustomerJourney() {
  const feed = useAccounts()
  const { data, status, reload, demo, source } = feed
  const [phase, setPhase] = useState(null)
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(null) // deal for detail modal
  const [rep, setRep] = useState('')
  /* THE LANE LEADS, and that ordering is the point. Opening on the account
   * board answers "where is everything", which is the question that made this
   * screen feel dead. Opening on the lane answers "who is waiting on me". */
  const [view, setView] = useState('lane')

  const counts = useMemo(() => countByPhase(data), [data])
  const reps = useMemo(() => repRollup(data), [data])
  /* The shared date search, on the order's DUE date (2026-10-26). An order with
   * no due date stays on the board, flagged in the summary line. */
  const df = useDateFilter('orders')
  const dated = useMemo(() => df.apply(filterDeals(data, { phase, query, rep })), [data, phase, query, rep, df])
  const board = dated.rows

  const summary = useMemo(() => ({
    total: data.length,
    approvals: data.filter((d) => d.phase === 'approvals').length,
    production: data.filter((d) => d.phase === 'production').length,
    attention: data.filter((d) => d.phase === 'hold' || d.atRisk).length,
  }), [data])

  const loading = status === 'loading'
  const unavailable = status === 'unavailable'
  const failed = status === 'error'
  const noFeed = unavailable || failed

  const provenance = demo
    ? 'Demo data'
    : loading ? 'Connecting…'
    : source ? `Returned feed · ${source.label} · read-only`
    : 'Feed unavailable · read-only'

  return (
    <div className="fade-up">
      <PageHeader
        eyebrow="Internal · manufacturing"
        title={INTERNAL_LABEL}
        lead="Where every order is on SR’s floor — which are blocked, how late they are, and who to chase. Internal only."
        actions={
          <span className={styles.readonly} data-state={noFeed ? 'off' : 'on'}>
            <span className={styles.readonlyDot} />
            {provenance}
          </span>
        }
      />

      {/* ABOVE THE WORK ON PURPOSE. A rep who opened the wrong one of these two
        * screens has to find out before they start reading it. Every word comes
        * from lib/surfaces.js, which the Team Meeting Room page reads too — two
        * screens explaining themselves from one string cannot drift apart. */}
      <PurposeStrip which="internal" />

      {demo && (
        <div className={styles.demoBanner}>
          <strong>Demo data</strong> — fictional deals for layout preview (<code>?demo=1</code>).
          The live order feed loads without this flag.
        </div>
      )}

      <JourneyCoverageNotice feed={feed} />
      {noFeed && <button type="button" className="btn" onClick={reload}>Retry order feed</button>}

      {/* Source order-owner filters apply to the returned board, not the separately scoped lane. */}
      <Segmented
        options={[
          { key: 'lane', label: 'Needs the customer' },
          { key: 'board', label: 'Order feed' },
        ]}
        value={view}
        onChange={setView}
        ariaLabel="Journey view"
      />

      {view === 'board' && !loading && !noFeed && (
        <RepBar
          reps={reps}
          value={rep}
          onChange={setRep}
          totalAccounts={data.length}
          /* Counts describe the returned board subset only. */
          showCounts={view === 'board'}
        />
      )}

      {view === 'lane' && <LaneBoard />}

      {view === 'board' && (
      <>
      {/* These four count the returned order rows, which are a different set from
        * the one the lane shows. They render only on the view they describe —
        * side by side they read as the same screen disagreeing with itself. */}
      <div className={styles.stats}>
        <Stat n={loading || noFeed ? 'Unmeasured' : summary.total} label="Returned rows" />
        <Stat n={loading || noFeed ? 'Unmeasured' : summary.approvals} label="Returned: in approvals" tone="warn" />
        <Stat n={loading || noFeed ? 'Unmeasured' : summary.production} label="Returned: in production" />
        <Stat n={loading || noFeed ? 'Unmeasured' : summary.attention} label="Returned: needs attention" tone="danger" />
      </div>

      <div className={`card ${styles.funnelCard}`}>
        {loading
          ? <div className={styles.skelRow} style={{ height: 120 }} />
          : noFeed ? <p>Phase counts unavailable, not zero.</p> : <JourneyFunnel counts={counts} selected={phase} onSelect={setPhase} />}
      </div>

      <div className={styles.boardHead}>
        <div className={styles.boardTitle}>
          {phase ? PHASE_LABEL[phase] : 'Returned order rows'}
          {phase && <button className="chip" style={{ cursor: 'pointer' }} onClick={() => setPhase(null)}>✕ clear</button>}
        </div>
        <div className={styles.controls}>
          <input
            className={`input ${styles.search}`}
            type="search"
            placeholder="Search accounts, orders, owners…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search accounts"
          />
          <span className={styles.count}>{loading ? 'Loading…' : noFeed ? 'Count unavailable' : `${board.length} returned rows shown`}</span>
        </div>
      </div>

      {!loading && !noFeed && <DateFilter {...df.props} summary={dated.summary} noun="orders" />}

      {loading ? (
        <div className={styles.skelGrid}>{Array.from({ length: 6 }).map((_, i) => <div key={i} className={styles.skelRow} style={{ height: 120 }} />)}</div>
      ) : noFeed ? <p>Accounts cannot be read from this order feed right now.</p> : (
        <AccountBoard
          items={board}
          onOpen={setOpen}
          emptyHint={
            noFeed ? 'Order feed unavailable, not an empty account book.'
              : phase ? `No orders in “${PHASE_LABEL[phase]}” right now.`
              : query ? 'No accounts match your search.'
              : 'No active orders in the returned subset. Full-book coverage is not established.'
          }
        />
      )}
      </>
      )}

      {/* Wrapped because a throw inside the detail used to unmount the whole
        * app — the "gray screen" when you clicked an account. Now the modal
        * reports what failed and the board behind it survives. */}
      {open && (
        <ErrorBoundary label="The account detail" onReset={() => setOpen(null)}>
        <AccountDetail
          deal={open}
          onClose={() => setOpen(null)}
          /* A status move changes the board, so the board is re-read. Without
           * this the modal reports success while every card behind it still
           * shows the old status, which is exactly how people stop trusting a
           * screen. */
          onChanged={reload}
        />
        </ErrorBoundary>
      )}
    </div>
  )
}

function Stat({ n, label, tone }) {
  return (
    <div className={`card ${styles.stat}`} data-tone={tone || ''}>
      <div className={styles.statNum}>{n}</div>
      <div className={styles.statLabel}>{label}</div>
    </div>
  )
}

